import * as React from 'react';
import { useLocation } from 'react-router-dom';

import Button from 'basics/buttons/Button';
import { DotsLoader } from 'basics/loaders';
import AssetPairLoading from 'basics/loaders/AssetPairLoading/AssetPairLoading';
import ContentPlaceholder from 'basics/loaders/ContentPlaceholder/ContentPlaceholder';
import {
    Bar,
    SkeletonRow,
    SkeletonStack,
} from 'basics/loaders/ContentPlaceholder/ContentPlaceholder.styled';
import SectionPicker from 'basics/SectionPicker';

import {
    Chart,
    Charts,
    Container,
    Content,
    ListBlock,
    ListHeader,
    ListTotal,
    PlusIcon,
    Section,
} from 'pages/amm/pages/Analytics.styled';

import { Header, SelectPlaceholder, SearchPlaceholder, FilterPlaceholder } from './AllPools.layout';
import {
    ChartGrid,
    ChartHeading,
    ChartPlot,
    PoolIdentity,
    PoolRow,
    PoolValue,
    TableHeader,
} from './AllPoolsLoading.styled';

import { FilterOptions } from '../MyLiquidity/MyLiquidity.constants';
import {
    PoolsListBlock,
    ToggleGroupStyled,
    SelectWrapper,
    SelectStyled,
    ListContent,
} from '../MyLiquidity/MyLiquidity.styled';
import MyLiquidityLoading from '../MyLiquidity/MyLiquidityLoading';

export const StatsChartLoading = ({ label }: { label: string }) => (
    <ContentPlaceholder label={`Loading ${label}`}>
        <ChartPlot aria-hidden="true">
            <ChartHeading>
                <SkeletonStack>
                    <span>{label}</span>
                    <Bar $width="13rem" $height="3.2rem" />
                </SkeletonStack>
                <Bar $width="12rem" $height="3.2rem" />
            </ChartHeading>
            <ChartGrid>
                {Array.from({ length: 4 }, (_, i) => (
                    <div key={i} />
                ))}
            </ChartGrid>
            <SkeletonRow>
                {Array.from({ length: 4 }, (_, i) => (
                    <Bar key={i} $width="3rem" />
                ))}
            </SkeletonRow>
        </ChartPlot>
    </ContentPlaceholder>
);

export const PoolsTableLoading = () => (
    <ContentPlaceholder label="Loading pools">
        <TableHeader aria-hidden="true">
            <span>Pool</span>
            <span>TVL</span>
            <span>Volume 24H</span>
            <span>Rewards</span>
            <span>Total APR</span>
        </TableHeader>
        {Array.from({ length: 5 }, (_, i) => (
            <PoolRow key={i} aria-hidden="true">
                <PoolIdentity>
                    <AssetPairLoading />
                    <SkeletonStack $gap="0.8rem">
                        <Bar $width="12rem" $height="2rem" />
                        <Bar $width="7rem" $height="1.6rem" />
                        <Bar $width="16rem" />
                    </SkeletonStack>
                </PoolIdentity>
                {['TVL:', 'Volume 24H:', 'Rewards', 'Total APR'].map(label => (
                    <PoolValue key={label}>
                        <span>{label}</span>
                        <Bar $width="7rem" $height="2rem" />
                    </PoolValue>
                ))}
            </PoolRow>
        ))}
    </ContentPlaceholder>
);

const AllPoolsLoading = () => {
    const { search } = useLocation();
    const params = new URLSearchParams(search);
    const isMyLiquidity = params.get('tab') === 'my';
    return (
        <Container>
            <Content>
                <Section>
                    <Charts>
                        <Chart data-testid="pools-volume-chart">
                            <StatsChartLoading label="Last 24H volume" />
                        </Chart>
                        <Chart data-testid="pools-liquidity-chart">
                            <StatsChartLoading label="Liquidity" />
                        </Chart>
                    </Charts>
                </Section>
                <Section>
                    <ListBlock data-testid="pools-list">
                        <ListHeader>
                            <div inert>
                                <SectionPicker
                                    options={[
                                        { label: 'All Pools', value: 'top' },
                                        { label: 'My Liquidity', value: 'my' },
                                    ]}
                                    value={isMyLiquidity ? 'my' : 'top'}
                                    onChange={() => {}}
                                />
                            </div>
                            {isMyLiquidity ? (
                                <ListTotal>
                                    <span>Total: </span>
                                    <span>
                                        <DotsLoader />
                                    </span>
                                </ListTotal>
                            ) : (
                                <Button disabled>
                                    create pool <PlusIcon />
                                </Button>
                            )}
                        </ListHeader>
                        {isMyLiquidity ? (
                            <PoolsListBlock $onlyList inert>
                                <ToggleGroupStyled
                                    value={params.get('filter') ?? 'all'}
                                    options={FilterOptions}
                                    onChange={() => {}}
                                />
                                <SelectWrapper>
                                    <SelectStyled
                                        value={params.get('filter') ?? 'all'}
                                        options={FilterOptions}
                                        onChange={() => {}}
                                    />
                                </SelectWrapper>
                                <ListContent>
                                    <MyLiquidityLoading />
                                </ListContent>
                            </PoolsListBlock>
                        ) : (
                            <>
                                <Header aria-hidden="true">
                                    <FilterPlaceholder>
                                        <Bar $width="36rem" $height="4.2rem" />
                                    </FilterPlaceholder>
                                    <SelectPlaceholder>
                                        <Bar $width="100%" $height="5.6rem" />
                                    </SelectPlaceholder>
                                    <SearchPlaceholder>
                                        <Bar $width="100%" $height="5.6rem" />
                                    </SearchPlaceholder>
                                </Header>
                                <PoolsTableLoading />
                            </>
                        )}
                    </ListBlock>
                </Section>
            </Content>
        </Container>
    );
};

export default AllPoolsLoading;
